// MBA Lab — Compassionate Accountability Planner UI. Imports only lib/core.js,
// lib/planner-modes.js, lib/planner-protocol.js, lib/planner-model.js,
// lib/message-checks.js (the same rules-based check engine the scenario
// simulator's write nodes use, for draft-message-review mode's automated
// checks), and lib/tool-dock.js. Mode-picker cards, a ?mode=
// deep link (UI-only, canonical unaffected - matches the ROI calculator's
// prefill precedent), single store key with modeId in state, and a
// restore-bar-style confirm before a mode switch discards the current draft.
//
// Persistence is tiered, not a single store like every other tool: autosave
// always writes the full draft to sessionStorage (gone once the tab closes),
// since that's ephemeral enough to safely hold real drafted text. A separate
// opt-in "remember on this device" toggle additionally writes to localStorage
// - but only the structural/progress state (stripDraftText()), never the
// actual conversation text, so a specific real draft about a real person
// never sits on disk indefinitely just because the tab happened to stay open.
import { store, sessionStore, download, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import { MODES, MODES_BY_ID, DEFAULT_MODE_ID, isValidModeId } from './lib/planner-modes.js';
import { STEPS, SYSTEM_CAUSES, SAFETY_CATEGORIES } from './lib/planner-protocol.js';
import { runChecks } from './lib/message-checks.js';
import {
  SCHEMA_VERSION,
  createInitialState,
  setTextField,
  setRegulated,
  setSafetyFlag,
  setSystemCause,
  setRubricItem,
  setMode,
  parseSavedState,
  serializeForSave,
  countStepsStarted,
  hasRealUserContent,
  safetyScreen,
  buildDocumentModel,
  buildMarkdown,
  stripDraftText,
  REVIEW_MODE_OPENING_LEGEND,
  REVIEW_MODE_OPENING_LABEL,
  REVIEW_MODE_OPENING_CHECKS_LABEL,
} from './lib/planner-model.js';
import { initToolDock } from './lib/tool-dock.js';

const DEFAULT_OPENING_LEGEND = 'Their perspective';
const DEFAULT_OPENING_LABEL = 'Suggested opening';
const DEFAULT_OPENING_CHECKS_LABEL = 'Your suggested opening, above';

const TOOL_NAME = 'accountability-planner';
const AUTOSAVE_DEBOUNCE_MS = 400;
const TOTAL_STEPS = STEPS.length;

// draft-message-review mode's automated checks, split across its two
// repurposed fields (see planner-modes.js's 'draft-message-review' entry -
// both suggestedOpening and suggestedQuestions are blank there specifically
// so this mode can repurpose the same two already-labeled fields). The
// message checks run against the whole "Suggested opening" field; the
// question checks run against only the first line of "Questions to ask"
// (see renderDraftChecks) since isOpenQuestion/underSevenWords are shaped
// for one short question, not the multi-question block every other mode's
// copy models. All 11 checks from lib/message-checks.js are used across the
// two lists - none dropped, none duplicated.
const DRAFT_MESSAGE_CHECK_IDS = Object.freeze([
  'avoidsYouStem', 'hasObservable', 'oneEmotion', 'noJudgmentAsEmotion', 'hasRequest', 'hasSpecificDate', 'endsWithQuestion', 'noAbsolutes',
]);
const DRAFT_QUESTIONS_CHECK_IDS = Object.freeze(['isOpenQuestion', 'underSevenWords', 'noAdvice']);

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

function currentMode(state) {
  return MODES_BY_ID.get(state.modeId) || MODES_BY_ID.get(DEFAULT_MODE_ID);
}

/** Resolves a mode's rubric length for sanitizeState's bounds-check on
 * rubricChecked - see planner-model.js's sanitizeState doc comment. */
function getRubricLength(modeId) {
  return MODES_BY_ID.get(modeId)?.rubric?.length ?? null;
}

class AccountabilityPlanner {
  constructor() {
    this.cacheElements();
    this.sessionStore = sessionStore(TOOL_NAME);
    // ONLY the persistent tier is wrapped, and that is the whole design: everything written here
    // has already been through stripDraftText, so the drafted conversation about a real person
    // cannot reach the account by any path. The session tier - which holds the full draft - is
    // untouched and never leaves this tab. The server re-checks those fields are blank regardless.
    this.persistentStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.rememberPrefStore = store(`${TOOL_NAME}-remember`);
    this.remember = this.rememberPrefStore.load() === true;
    this.els.rememberToggle.checked = this.remember;
    this.rafId = null;
    this.saveTimer = null;
    this.hasEngaged = false;
    this.hasFiredResult = false;
    this.modeSwitchConfirmId = null;
    this.modeSwitchTimer = null;
    this.dock = initToolDock({
      dock: byId('planner-dock'),
      jumpTarget: byId('planner-document'),
      returnTarget: byId('planner-form'),
    });

    this.state = this.loadInitialState();
    this.applyDeepLinkMode();
    this.renderAll();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('planner-form');
    this.els = {
      modeGrid: byId('planner-mode-grid'),
      modeSwitchNote: byId('planner-mode-switch-note'),
      modeIntro: byId('planner-mode-intro'),

      safetyGrid: byId('planner-safety-grid'),
      safetyHint: byId('planner-safety-hint'),
      safetyBanner: byId('planner-safety-banner'),
      regulated: byId('planner-regulated'),

      facts: byId('planner-facts'),
      assumptions: byId('planner-assumptions'),
      standard: byId('planner-standard'),
      impact: byId('planner-impact'),
      opening: byId('planner-opening'),
      questions: byId('planner-questions'),
      openingLegend: byId('planner-opening-legend'),
      openingLabel: byId('planner-opening-label'),
      openingChecksLabel: byId('planner-opening-checks-label'),

      causesGrid: byId('planner-causes-grid'),
      causesHint: byId('planner-causes-hint'),
      causesNotes: byId('planner-causes-notes'),

      support: byId('planner-support'),
      commitments: byId('planner-commitments'),
      reviewTiming: byId('planner-review-timing'),
      consequences: byId('planner-consequences'),
      consequencesField: byId('planner-consequences-field'),
      risks: byId('planner-risks'),

      conflictExtra: byId('planner-extra-conflict'),
      conflictPrompt: byId('planner-conflict-prompt'),
      conflictBreakdown: byId('planner-conflict-breakdown'),

      agendaExtra: byId('planner-extra-agenda'),
      agendaBreakdown: byId('planner-agenda-breakdown'),

      rubricExtra: byId('planner-extra-rubric'),
      rubricList: byId('planner-rubric-list'),
      draftMessageChecks: byId('planner-draft-message-checks'),
      draftQuestionsChecks: byId('planner-draft-questions-checks'),
      // Only present in the DOM at all when LeadershipAi:Enabled is true - see
      // AccountabilityPlanner.cshtml's data-ai-review gating. byId() returns null
      // otherwise, which every use below already guards against.
      aiReviewButton: byId('planner-ai-review-button'),
      aiReviewResult: byId('planner-ai-review-result'),

      progressText: byId('planner-progress-text'),
      startOver: byId('planner-start-over'),
      rememberToggle: byId('planner-remember-toggle'),
      print: byId('planner-print'),
      download: byId('planner-download'),
      copy: byId('planner-copy'),
      actionFeedback: byId('planner-action-feedback'),

      document: byId('planner-document'),
      linkedTools: byId('planner-linked-tools'),
    };
  }

  /** Session storage wins when both exist - it's the same-tab-session draft,
   * still in progress. The persistent tier only matters once the session one
   * is gone (a new tab session, after the user opted in previously), and by
   * design already has its free-text fields blanked. */
  loadInitialState() {
    const saved = this.sessionStore.load() || this.persistentStore.load();
    if (!saved) return createInitialState(DEFAULT_MODE_ID, MODES_BY_ID.get(DEFAULT_MODE_ID));

    const result = parseSavedState(saved, isValidModeId, DEFAULT_MODE_ID, getRubricLength);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState(DEFAULT_MODE_ID, MODES_BY_ID.get(DEFAULT_MODE_ID));
  }

  /** UI-only prefill, matching the ROI calculator's ?school=/?program= precedent
   * - never touches the canonical URL. Only applies on a fresh (unsaved) load,
   * never silently overwrites a returning user's in-progress plan. */
  applyDeepLinkMode() {
    if (this.sessionStore.load() || this.persistentStore.load()) return;
    const params = new URLSearchParams(window.location.search);
    const requestedMode = params.get('mode');
    if (requestedMode && isValidModeId(requestedMode)) {
      this.state = createInitialState(requestedMode, MODES_BY_ID.get(requestedMode));
    }
  }

  wireEvents() {
    this.els.modeGrid.addEventListener('click', (e) => {
      const card = e.target.closest('[data-planner-mode-card]');
      if (card) this.handleModeCardClick(card.dataset.plannerModeCard);
    });

    this.form.addEventListener('input', (e) => this.handleFieldChange(e));
    this.form.addEventListener('change', (e) => this.handleFieldChange(e));

    this.els.startOver.addEventListener('click', () => this.handleStartOver());
    this.els.rememberToggle.addEventListener('change', () => this.handleRememberToggle(this.els.rememberToggle.checked));
    this.els.print.addEventListener('click', () => this.handlePrint());
    this.els.download.addEventListener('click', () => this.handleDownload());
    this.els.copy.addEventListener('click', () => this.handleCopy());

    if (this.els.aiReviewButton) {
      this.els.aiReviewButton.addEventListener('click', () => this.handleAiReviewClick());
    }
  }

  handleFieldChange(e) {
    const el = e.target;
    this.markEngaged();

    if (el.matches('[data-planner-text-field]')) {
      this.state = setTextField(this.state, el.dataset.plannerTextField, el.value);
    } else if (el === this.els.regulated) {
      this.state = setRegulated(this.state, el.checked);
    } else if (el.matches('[data-planner-safety]')) {
      this.state = setSafetyFlag(this.state, el.dataset.plannerSafety, el.checked);
    } else if (el.matches('[data-planner-cause]')) {
      this.state = setSystemCause(this.state, el.dataset.plannerCause, el.checked);
    } else if (el.matches('[data-planner-rubric-index]')) {
      this.state = setRubricItem(this.state, Number(el.dataset.plannerRubricIndex), el.checked);
    } else {
      return;
    }

    this.scheduleUpdate();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  // --- Mode switching ---------------------------------------------

  handleModeCardClick(modeId) {
    if (!isValidModeId(modeId) || modeId === this.state.modeId) return;

    // Not countStepsStarted() - every fresh plan already has 2 "started" steps
    // from the mode's own auto-seeded opening/questions/support text, which
    // would make the very first mode switch on a brand-new plan warn about
    // discarding a "draft" that's actually just untouched placeholder text.
    const hasContent = hasRealUserContent(this.state, currentMode(this.state));
    if (hasContent && this.modeSwitchConfirmId !== modeId) {
      this.modeSwitchConfirmId = modeId;
      const modeLabel = MODES_BY_ID.get(modeId).label;
      this.els.modeSwitchNote.hidden = false;
      this.els.modeSwitchNote.textContent = `Switch to "${modeLabel}"? This starts a new plan - your current draft will be cleared. Click that mode again to confirm.`;
      if (this.modeSwitchTimer) clearTimeout(this.modeSwitchTimer);
      this.modeSwitchTimer = setTimeout(() => {
        this.modeSwitchConfirmId = null;
        this.els.modeSwitchNote.hidden = true;
      }, 5000);
      return;
    }

    if (this.modeSwitchTimer) clearTimeout(this.modeSwitchTimer);
    this.modeSwitchConfirmId = null;
    this.els.modeSwitchNote.hidden = true;

    this.state = setMode(this.state, modeId, MODES_BY_ID.get(modeId));
    this.hasFiredResult = false;
    this.renderAll();
    this.scheduleAutosave();
    trackToolAction(TOOL_NAME, 'engage', { mode_id: modeId });
  }

  // --- Rendering ---------------------------------------------

  renderAll() {
    this.renderModeGrid();
    this.renderModeIntro();
    this.applyStateToForm();
    this.renderModeSpecificExtras();
    this.renderSystemCausesHint();
    this.renderSafetyHint();
    this.renderSafetyBanner();
    this.renderDocumentPreview();
    this.renderProgress();
    this.renderLinkedTools();
  }

  renderModeGrid() {
    clearChildren(this.els.modeGrid);
    for (const mode of MODES) {
      const isActive = mode.id === this.state.modeId;
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'tool-planner-mode-card' + (isActive ? ' is-active' : '');
      card.setAttribute('aria-pressed', String(isActive));
      card.dataset.plannerModeCard = mode.id;

      const label = document.createElement('span');
      label.className = 'tool-planner-mode-card-label';
      label.textContent = mode.label;
      card.appendChild(label);

      const tagline = document.createElement('span');
      tagline.className = 'tool-planner-mode-card-tagline';
      tagline.textContent = mode.tagline;
      card.appendChild(tagline);

      this.els.modeGrid.appendChild(card);
    }
  }

  renderModeIntro() {
    this.els.modeIntro.textContent = currentMode(this.state).intro;
  }

  applyStateToForm() {
    this.form.querySelectorAll('[data-planner-text-field]').forEach((el) => {
      el.value = this.state[el.dataset.plannerTextField] || '';
    });
    this.els.regulated.checked = this.state.regulated;
    this.form.querySelectorAll('[data-planner-safety]').forEach((el) => {
      el.checked = this.state.safetyChecklist[el.dataset.plannerSafety] === true;
    });
    this.form.querySelectorAll('[data-planner-cause]').forEach((el) => {
      el.checked = this.state.systemCauses.includes(el.dataset.plannerCause);
    });
  }

  renderModeSpecificExtras() {
    const mode = currentMode(this.state);

    this.els.conflictExtra.hidden = !mode.conflictBreakdownPrompt;
    if (mode.conflictBreakdownPrompt) this.els.conflictPrompt.textContent = mode.conflictBreakdownPrompt;

    this.els.agendaExtra.hidden = !mode.agendaTopics;

    this.els.rubricExtra.hidden = !mode.rubric;
    if (mode.rubric) this.renderRubricList(mode.rubric);
    if (mode.rubric) this.renderDraftChecks();

    // draft-message-review repurposes the opening field for reviewing an
    // already-written message rather than drafting a new one - swap its
    // legend/label/checks-header to match (audit finding). Same mode.rubric
    // gate as the rest of this method, since it's the one mode with a rubric.
    this.els.openingLegend.textContent = mode.rubric ? REVIEW_MODE_OPENING_LEGEND : DEFAULT_OPENING_LEGEND;
    this.els.openingLabel.textContent = mode.rubric ? REVIEW_MODE_OPENING_LABEL : DEFAULT_OPENING_LABEL;
    this.els.openingChecksLabel.textContent = mode.rubric ? REVIEW_MODE_OPENING_CHECKS_LABEL : DEFAULT_OPENING_CHECKS_LABEL;
  }

  /** Live, deterministic feedback on the two draft-message-review fields -
   * simple rules-based checks (lib/message-checks.js), never saved, never
   * sent anywhere. Distinct from the optional AI review below: this always
   * runs, with no server call and no flag to enable. */
  renderDraftChecks() {
    this.renderCheckList(this.els.draftMessageChecks, this.els.opening.value, DRAFT_MESSAGE_CHECK_IDS);
    // Only the FIRST line, not the whole field: this field holds one or more
    // questions (every other mode's copy models 2-3 on separate lines), but
    // isOpenQuestion/underSevenWords are shaped for a single short question.
    // Checking the full multi-line block against them was a real miscalibration
    // found by audit - a realistic 2-question draft failed underSevenWords
    // outright, and isOpenQuestion only ever looked at the first question's
    // opening words regardless of how many followed. Scoping to line one and
    // labeling it that way in the Razor copy makes the check honestly match
    // what it actually evaluates.
    const firstQuestion = this.els.questions.value.split('\n')[0] ?? '';
    this.renderCheckList(this.els.draftQuestionsChecks, firstQuestion, DRAFT_QUESTIONS_CHECK_IDS);
  }

  renderCheckList(listEl, text, checkIds) {
    clearChildren(listEl);
    for (const check of runChecks(text, checkIds)) {
      const li = document.createElement('li');
      li.className = check.passed ? 'tool-planner-draft-check is-pass' : 'tool-planner-draft-check';
      li.textContent = check.label;
      listEl.appendChild(li);
    }
  }

  renderRubricList(rubric) {
    clearChildren(this.els.rubricList);
    rubric.forEach((item, index) => {
      const label = document.createElement('label');
      label.className = 'tool-planner-rubric-item';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.dataset.plannerRubricIndex = String(index);
      input.checked = this.state.rubricChecked.includes(index);
      label.appendChild(input);
      label.appendChild(document.createTextNode(' ' + item));
      this.els.rubricList.appendChild(label);
    });
  }

  renderSystemCausesHint() {
    const mode = currentMode(this.state);
    if (mode.emphasizedCauses.length === 0) {
      this.els.causesHint.hidden = true;
      return;
    }
    const labels = mode.emphasizedCauses
      .map((id) => SYSTEM_CAUSES.find((c) => c.id === id)?.label)
      .filter(Boolean)
      .join(', ');
    this.els.causesHint.hidden = false;
    this.els.causesHint.textContent = `Common in this situation: ${labels}. Not pre-selected - use your own judgment.`;
  }

  /** Mirrors renderSystemCausesHint() exactly, for the sibling
   * mode.safetyEmphasis field - this data existed since the mode-data model
   * was built but was never actually rendered anywhere in the UI. */
  renderSafetyHint() {
    const mode = currentMode(this.state);
    if (mode.safetyEmphasis.length === 0) {
      this.els.safetyHint.hidden = true;
      return;
    }
    const labels = mode.safetyEmphasis
      .map((id) => SAFETY_CATEGORIES.find((c) => c.id === id)?.label)
      .filter(Boolean)
      .join(', ');
    this.els.safetyHint.hidden = false;
    this.els.safetyHint.textContent = `Worth double-checking for this situation: ${labels}.`;
  }

  renderSafetyBanner() {
    const screen = safetyScreen(this.state);
    this.els.consequencesField.classList.toggle('is-gated', screen.level !== 'ok');
    this.els.consequences.disabled = screen.level !== 'ok';

    if (screen.level === 'ok') {
      this.els.safetyBanner.hidden = true;
      return;
    }
    this.els.safetyBanner.hidden = false;
    this.els.safetyBanner.classList.toggle('tool-planner-safety-banner--stop', screen.level === 'stop');
    this.els.safetyBanner.textContent = screen.guidance;
  }

  renderProgress() {
    const count = countStepsStarted(this.state, currentMode(this.state));
    const text = `${count} of ${TOTAL_STEPS} steps started`;
    this.els.progressText.textContent = text;
    this.dock?.setReading(text);
    this.dock?.setComplete(count === TOTAL_STEPS);
  }

  renderLinkedTools() {
    clearChildren(this.els.linkedTools);
    const mode = currentMode(this.state);
    if (mode.linkedTools.length === 0) {
      this.els.linkedTools.hidden = true;
      return;
    }
    this.els.linkedTools.hidden = false;
    for (const tool of mode.linkedTools) {
      const a = document.createElement('a');
      a.href = `/tools/${tool.slug}`;
      a.className = 'btn btn-outline-secondary btn-sm';
      a.textContent = `Open ${tool.label} →`;
      this.els.linkedTools.appendChild(a);
    }
  }

  scheduleUpdate() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.renderSystemCausesHint();
      this.renderSafetyBanner();
      this.renderDocumentPreview();
      this.renderProgress();
      // Only draft-message-review has the fields these checks read from -
      // matches renderModeSpecificExtras()'s own mode.rubric guard, so this
      // doesn't do pointless work on every keystroke in every other mode.
      if (currentMode(this.state).rubric) this.renderDraftChecks();
      this.scheduleAutosave();
      this.maybeTrackResult();
    });
  }

  maybeTrackResult() {
    if (this.hasFiredResult) {
      // Coarse params only - which mode and step-progress count, never the
      // user's actual typed content.
      trackToolAction(TOOL_NAME, 'result', { mode_id: this.state.modeId, steps_started: countStepsStarted(this.state, currentMode(this.state)) });
    }
    this.hasFiredResult = true;
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.sessionStore.save(toSave);
    if (this.remember) {
      this.persistentStore.save(stripDraftText(toSave));
    }
  }

  /** Fires immediately on toggle, not on the next debounced autosave - so
   * turning "remember" off actually clears the persistent copy right away,
   * rather than leaving it stale if the tab closes before the next autosave. */
  async handleRememberToggle(checked) {
    this.remember = checked === true;
    this.rememberPrefStore.save(this.remember);
    if (!this.remember) {
      // "Remember" is the consent for BOTH tiers of durability - this device and the account.
      // Turning it off has to remove the account's copy too, or the thing the user just asked to
      // forget survives on a server and reappears on their next device.
      this.persistentStore.deleteAccountState();
      return;
    }

    // HYDRATE BEFORE THE FIRST PERSIST. The page-load hydrate is gated on this same toggle, so a
    // fresh device turning it on has never fetched - and on that device the toggle plausibly MEANS
    // "get my stuff". Persisting first would stamp the untouched session with a now() clock,
    // strictly newer than the account's copy, and the server would dutifully replace the person's
    // real structural tier with this device's defaults. Fetch first; only a session holding real
    // user content earns the right to overwrite.
    await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION).catch(() => {});
    if (hasRealUserContent(this.state, currentMode(this.state))) {
      this.persistentStore.save(stripDraftText({ ...this.state, savedAt: Date.now() }));
    }
  }

  // --- Document preview ---------------------------------------------

  renderDocumentPreview() {
    const mode = currentMode(this.state);
    const doc = buildDocumentModel(this.state, mode);
    clearChildren(this.els.document);

    const header = document.createElement('header');
    header.className = 'tool-planner-doc-header';
    const title = document.createElement('h2');
    title.textContent = mode.label;
    header.appendChild(title);
    this.els.document.appendChild(header);

    for (const section of doc.sections) {
      const sectionEl = document.createElement('section');
      sectionEl.className = 'tool-planner-doc-section';

      const h3 = document.createElement('h3');
      h3.textContent = section.title;
      sectionEl.appendChild(h3);

      for (const block of section.blocks) {
        if (block.type === 'p') {
          const p = document.createElement('p');
          p.textContent = block.text;
          sectionEl.appendChild(p);
        } else if (block.type === 'list') {
          const ul = document.createElement('ul');
          for (const item of block.items) {
            const li = document.createElement('li');
            li.textContent = item;
            ul.appendChild(li);
          }
          sectionEl.appendChild(ul);
        }
      }

      this.els.document.appendChild(sectionEl);
    }

    const footer = document.createElement('footer');
    footer.className = 'tool-planner-doc-footer';
    footer.textContent = 'Built with the free MBA Lab Compassionate Accountability Planner — mbalab.net/tools/accountability-planner';
    this.els.document.appendChild(footer);
  }

  // --- Actions ---------------------------------------------

  handleStartOver() {
    if (!this.startOverConfirming) {
      this.startOverConfirming = true;
      this.els.startOver.textContent = 'Click again to confirm';
      this.startOverTimer = setTimeout(() => {
        this.startOverConfirming = false;
        this.els.startOver.textContent = 'Start over';
      }, 4000);
      return;
    }

    clearTimeout(this.startOverTimer);
    this.startOverConfirming = false;
    this.els.startOver.textContent = 'Start over';
    this.sessionStore.clear();
    this.persistentStore.deleteAccountState();
    this.state = createInitialState(this.state.modeId, currentMode(this.state));
    this.hasFiredResult = false;
    this.renderAll();
  }

  handlePrint() {
    trackToolAction(TOOL_NAME, 'print');
    this.showActionFeedback('Opening print…');
    window.print();
  }

  handleDownload() {
    const mode = currentMode(this.state);
    const doc = buildDocumentModel(this.state, mode);
    const markdown = buildMarkdown(doc, mode.label);
    const slug = mode.id;
    download(`accountability-plan-${slug}.md`, 'text/markdown', markdown);
    this.showActionFeedback('Downloaded.');
    trackToolAction(TOOL_NAME, 'download');
  }

  async handleCopy() {
    const mode = currentMode(this.state);
    const doc = buildDocumentModel(this.state, mode);
    const markdown = buildMarkdown(doc, mode.label);
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(markdown);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try downloading instead.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  /** Only reachable when the button exists at all (LeadershipAi:Enabled) - see
   * cacheElements(). Sends only the draft message text itself, nothing else from
   * the plan, matching the endpoint's own no-content-logging discipline. */
  async handleAiReviewClick() {
    const draftMessage = this.els.opening.value.trim();
    if (!draftMessage) {
      this.renderAiReviewResult('Write your draft message above first, then ask for feedback.', 'note');
      return;
    }

    this.els.aiReviewButton.disabled = true;
    this.els.aiReviewButton.textContent = 'Getting feedback…';
    this.renderAiReviewResult(null, 'hidden');

    try {
      const response = await fetch('/api/leadership/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draftMessage }),
      });

      if (response.status === 429) {
        this.renderAiReviewResult('Too many requests - please wait a minute and try again.', 'error');
      } else if (!response.ok) {
        this.renderAiReviewResult('The review service is temporarily unavailable. Please try again shortly.', 'error');
      } else {
        const data = await response.json();
        this.renderAiReviewResult(data.feedback, 'success');
        trackToolAction(TOOL_NAME, 'ai_review');
      }
    } catch {
      this.renderAiReviewResult('Could not reach the review service. Check your connection and try again.', 'error');
    } finally {
      this.els.aiReviewButton.disabled = false;
      this.els.aiReviewButton.textContent = 'Get AI feedback on this draft';
    }
  }

  renderAiReviewResult(text, kind) {
    const el = this.els.aiReviewResult;
    if (!el) return;
    if (kind === 'hidden') {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = text;
    el.classList.toggle('tool-planner-ai-review-result--error', kind === 'error');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('planner-form')) return;
  // Gated on the SAME opt-in that governs this device's own persistent tier: with "remember" off,
  // there is no request at all, in either direction. Turning it on is what starts syncing, which
  // keeps one switch meaning one thing - "keep this past today" - rather than two.
  if (store(`${TOOL_NAME}-remember`).load() === true) {
    await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  }
  new AccountabilityPlanner();
});
